import { useMemoAsync } from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { ScheduleDataObjectType, scheduleIterator } from "@opendash/schedule";
import dayjs from "dayjs";
import Parse from "parse";
import {
  Schedule_Execution,
  Schedule_Template,
} from "../../../js/parsegenerated";

export type ScheduleData = {
  template: Schedule_Template;
  finishedExecutions: Schedule_Execution[];
  runningExecutions: Schedule_Execution[];
  sources: Source[];
  nextExecution?: Date;
};

/**
 *  Fetches all schedule templates and their related data.
 * @returns all templates and the required data
 */
export const useScheduleData = (
  onQuery?: (
    query: Parse.Query<Schedule_Template>
  ) => Parse.Query<Schedule_Template>
) => {
  const scheduleData: ScheduleData[] = useMemoAsync(async () => {
    let query = new Parse.Query(Schedule_Template);
    query = onQuery?.(query) ?? query;
    const templates = await query.find();
    const sources = await Promise.all(
      templates.map(async (template) =>
        template.relation("sources")?.query().find()
      )
    );

    const allExecutions = await new Parse.Query(Schedule_Execution)
      .exists("origin")
      .exists("source")
      .descending("finishedAt")
      .includeAll()
      .limit(1_000_000)
      .find();

    const finishedExecutions = [];
    const runningExecutions = [];
    for (const exe of allExecutions) {
      if (exe.get("finishedAt")) finishedExecutions.push(exe);
      else runningExecutions.push(exe);
    }

    const data = templates.map(
      (template, index) =>
        ({
          template,
          finishedExecutions: allExecutions.filter(
            (exe) => exe.get("origin")?.id === template.id
          ),
          runningExecutions: runningExecutions.filter(
            (exe) => exe.get("origin")?.id === template.id
          ),
          sources: (sources[index] ?? []) as Source[],
        }) as ScheduleData
    );

    return data
      .map((d) => ({ ...d, nextExecution: getNextExecution(d) }))
      .sort((a, b) => {
        if (a.nextExecution < b.nextExecution) {
          return -1;
        }
        if (a.nextExecution > b.nextExecution) {
          return 1;
        }
        return 0;
      });
  }, []);

  return {
    scheduleData,
  };
};

/**
 * Returns the next execution by checking the last execution date and then compute the next one.
 * @param data the schedule data
 * @param sourceId optional. source id to get the next execution time for
 * @returns Date | undefined, if the iterator has no value found
 */
export function getNextExecution(data: ScheduleData, sourceId?: string) {
  try {
    const scheduleObject: ScheduleDataObjectType = {
      ...data.template.get("cron"),
    };

    const lastScheduleDate = getLastExecution(data, sourceId);

    // @Nico: Here we can decide if we want to use the last execution date or the start date from the schedule object
    // For now we always use the last execution date to compute the next one
    const iterator = scheduleIterator(scheduleObject);
    const nextScheduleDate = iterator?.nextDate(
      lastScheduleDate ?? scheduleObject.timestamp.startDate
    );

    return nextScheduleDate;
  } catch (e) {
    console.error("Error while getting next execution date:", e);
  }
  return undefined;
}

/**
 * Returns the last execution date for given data. if sourceId is set, get the last execution date for this specific source
 * @param data the schedule data
 * @param sourceId optional. source id to get the last specific execution date for
 * @returns Date or undefined, if this source has no execution for this template yet
 */
export function getLastExecution(data: ScheduleData, sourceId?: string) {
  let date: Date = undefined;
  for (const src of data.sources) {
    if (sourceId && src.id !== sourceId) continue;

    const lastSrcExecution = data.finishedExecutions.filter(
      (exe) => exe.get("source").id === src.id
    )?.[0];

    const finishedAt = lastSrcExecution?.get("finishedAt");

    if (sourceId && !finishedAt) continue; // No execution found for this source

    // if the current source has no execution, we take the run_startdate from the cron object
    date =
      !date || finishedAt < date
        ? (finishedAt ??
          (data.template.get("cron") as ScheduleDataObjectType).timestamp
            ?.startDate)
        : date;
  }
  return date;
}

/**
 * Takes the schedule data and checks if the next execution date is overdue.
 * If sourceId is set, it will check the next execution date for this specific source.
 * @param data the schedule data
 * @param sourceId optional. source id to check the next execution date for
 * @returns boolean, true if the schedule is overdue
 */
export function isScheduleOverdue(data: ScheduleData, sourceId?: string) {
  const date = sourceId ? getNextExecution(data, sourceId) : data.nextExecution;
  return !date || dayjs().add(-1, "day") > dayjs(date);
}

/**
 * Checks if the schedule is currently running.
 * @param data the schedule data
 * @param sourceId optional. source id to check if the schedule is running for this specific source
 * @returns boolean, true if the schedule is running
 */
export function isScheduleRunning(
  data: ScheduleData,
  sourceId?: string
): boolean {
  if (sourceId) {
    return data.runningExecutions.some(
      (exe) => exe.get("source").id === sourceId
    );
  }
  return data.runningExecutions.length > 0;
}
